import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Stack,
    DialogContent,
    DialogActions,
    Box,
    Divider,
    FormControl,
    FormLabel,
    IconButton,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { ArrowBack, UploadFile, Delete } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../../config/app.config';
import { useFileProcessor } from '../../../hooks/useFileProcessor';
import WarehouseDataPreview from '../../warehouse-data-preview/WarehouseDataPreview';
import { FilePreviewData } from '../../../services/types';
import { DataTransformationService } from '../../../services/DataTransformationService';
import FileDragDrop from '@shared/ui/common/FileDragDrop';

interface FileWarehouseFormProps {
    onClose: () => void;
    onBack: () => void;
    onNext?: (file: File, previewData: FilePreviewData, fileType: 'csv' | 'excel' | null) => void;
    fileData?: {
        file: File;
        previewData: FilePreviewData;
        fileType: 'csv' | 'excel' | null;
    } | null;
}


const FileWarehouseForm: React.FC<FileWarehouseFormProps> = ({
    onClose,
    onBack,
    onNext,
}) => {
    const [form, setForm] = useState({
        fileType: 'csv',
        file: null as File | null,
    });

    const [showPreviewPopup, setShowPreviewPopup] = useState(false);
    const [showMappingSuccess, setShowMappingSuccess] = useState(false);

    const {
        isProcessing,
        error,
        previewData,
        fileType,
        processFile,
        selectExcelSheet,
        updateColumnMapping,
        updateHeaderRowSettings,
        reprocessWithNewHeaderRow,
        reset,
    } = useFileProcessor();

    const handleRemoveFile = () => {
        setForm(prev => ({ ...prev, file: null }));
        reset();
    };

    const handleClose = () => {
        setForm({ fileType: 'csv', file: null });
        reset();
        onClose();
    };

    return (
        <>

            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center">
                        <IconButton onClick={onBack} color="inherit">
                            <ArrowBack />
                        </IconButton>

                        <Typography variant="h6" component="div">
                            File upload
                        </Typography>
                    </Stack>
                </Stack>
            </DialogHeader>
            <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
                <Stack gap={3} sx={{ mt: 2 }}>
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Box sx={{ width: 100, height: 60, borderRadius: 1, alignItems: 'center', justifyContent: 'center', display: 'flex', border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
                                <img
                                    src={'https://fra.cloud.appwrite.io/v1/storage/buckets/66df29f1002e1bc53bcb/files/687e07b000058ea6dc02/view?project=66548b010010dccec4ec&mode=admin'}
                                    alt={'csv'}
                                    style={{ width: '100%', height: 'auto' }}
                                    onError={(e) => {
                                        e.currentTarget.src = '/assets/images/ic_file.svg';
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="h6" color="text.primary">
                                    File-based Warehouse
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Create a warehouse by uploading inventory data from a file.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />

                    {!form.file ? (
                        <FileDragDrop
                            onFileSelect={async (file) => {
                                setForm(prev => ({ ...prev, file }));
                                await processFile(file);
                                setShowPreviewPopup(true);
                            }}
                            allowedFileTypes={['.csv', '.xlsx', '.xls']}
                            maxFileSize={10 * 1024 * 1024} // 10MB
                        >
                            <Stack alignItems="center" spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <UploadFile sx={{ color: APP_CONFIG.ui.theme.colors.primary }} />
                                    <Typography variant="h6" color={APP_CONFIG.ui.theme.colors.primary}>
                                        Upload file
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Drag and drop an Excel or CSV file here, or click to select
                                </Typography>
                            </Stack>
                        </FileDragDrop>
                    ) : (
                        <Stack spacing={2} >
                            {/* File Display Box */}
                            <Box sx={{
                                border: '1px solid',
                                borderColor: APP_CONFIG.ui.theme.colors.border,
                                borderRadius: 2,
                                p: 3,
                                backgroundColor: 'white',
                            }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    {/* File Info */}
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Box sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: 1,
                                            backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid',
                                            borderColor: APP_CONFIG.ui.theme.colors.border,
                                        }}>
                                            <UploadFile sx={{
                                                color: APP_CONFIG.ui.theme.colors.primary,
                                                fontSize: 28
                                            }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 500 }}>
                                                {form.file.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {fileType === 'csv' ? 'CSV File' : 'Excel File'}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Stack direction="row" spacing={3}>
                                        {previewData && !isProcessing && !error && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => setShowPreviewPopup(true)}
                                            >
                                                Edit Columns
                                            </Button>
                                        )}
                                        <Button
                                            variant="text"
                                            color="error"
                                            size="small"
                                            onClick={handleRemoveFile}
                                            startIcon={<Delete />}
                                        >
                                            Remove File
                                        </Button>
                                    </Stack>
                                </Stack>

                                {/* Processing Status */}
                                {isProcessing && (
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" color="text.secondary">
                                            Processing file...
                                        </Typography>
                                    </Stack>
                                )}

                                {/* Error Display */}
                                {error && (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                            </Box>
                        </Stack>
                    )}

                    {/* Preview Section - Similar to Bol Warehouse Form */}
                    {previewData && previewData.columnMapping?.ean && previewData.columnMapping?.quantity && (
                        <Box sx={{
                            backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint,
                            border: '1px solid',
                            borderColor: APP_CONFIG.ui.theme.colors.border,
                            borderRadius: 1,
                        }}>
                            <Typography p={2} variant="subtitle2" color="text.primary">
                                Preview of your inventory (grouped by EAN)
                            </Typography>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>EAN</TableCell>
                                            <TableCell align="right" width="100px">Total Quantity</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(() => {
                                            const eanColumnIndex = previewData.headers.findIndex(header => header === previewData.columnMapping?.ean);
                                            const quantityColumnIndex = previewData.headers.findIndex(header => header === previewData.columnMapping?.quantity);

                                            // Group by EAN and sum quantities
                                            const groupedData = new Map<string, { totalQuantity: number; rowCount: number }>();

                                            previewData.rows.forEach((row) => {
                                                const eanValue = eanColumnIndex >= 0 ? row[eanColumnIndex] : null;
                                                const quantityValue = quantityColumnIndex >= 0 ? row[quantityColumnIndex] : '';

                                                let eanString = typeof eanValue === 'number' ? String(eanValue) : eanValue;
                                                let quantityNumber = typeof quantityValue === 'string' ? parseFloat(quantityValue) : quantityValue;

                                                if (!eanString || !quantityNumber) return;

                                                const existing = groupedData.get(eanString);

                                                if (existing) {
                                                    existing.totalQuantity += quantityNumber;
                                                    existing.rowCount += 1;
                                                } else {
                                                    groupedData.set(eanString, {
                                                        totalQuantity: quantityNumber,
                                                        rowCount: 1
                                                    });
                                                }

                                            });

                                            // Convert to array and sort by total quantity (descending)
                                            const sortedData = Array.from(groupedData.entries())
                                                .sort(([, a], [, b]) => b.totalQuantity - a.totalQuantity);

                                            return sortedData.slice(0, 10).map(([eanValue, data]) => (
                                                <TableRow key={eanValue}>
                                                    <TableCell sx={{ maxWidth: 0, width: '60%' }}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.primary"
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    minWidth: 0
                                                                }}
                                                            >
                                                                {eanValue}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ width: '20%' }}>
                                                        <Typography variant="body2" color="text.primary">
                                                            {data.totalQuantity.toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ));
                                        })()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Stack >
            </DialogContent >

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        if (onNext && form.file && previewData?.columnMapping?.ean && previewData?.columnMapping?.quantity) {
                            onNext(form.file, previewData, fileType);
                        }
                    }}
                    variant="contained"
                    disabled={
                        !form.file ||
                        isProcessing ||
                        !!error ||
                        !previewData?.columnMapping?.ean ||
                        !previewData?.columnMapping?.quantity
                    }
                >
                    {onNext ? 'Next' : 'Create File Warehouse'}
                </Button>
            </DialogActions>

            {/* File Preview Popup */}
            {previewData && (
                <WarehouseDataPreview
                    open={showPreviewPopup}
                    options={{
                        canChangeHeaderRow: true
                    }}
                    onClose={() => {
                        setShowPreviewPopup(false);
                        // If no column mappings are set, remove the file
                        if (!previewData.columnMapping?.ean || !previewData.columnMapping?.quantity) {
                            handleRemoveFile();
                        } else {
                            setShowMappingSuccess(true);
                            // Hide success message after 3 seconds
                            setTimeout(() => setShowMappingSuccess(false), 3000);
                        }
                    }}
                    previewData={DataTransformationService.transformFileDataToAbstract(previewData)}
                    onColumnMappingChange={updateColumnMapping}
                    onHeaderRowSettingsChange={updateHeaderRowSettings}
                    onReprocessWithNewHeaderRow={reprocessWithNewHeaderRow}
                    onSheetSelect={async (sheetName: string) => {
                        if (form.file) {
                            await selectExcelSheet(sheetName, form.file);
                        }
                    }}
                    currentFile={form.file}
                />
            )}
        </>
    );
};

export default FileWarehouseForm; 